import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useEventListener } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { identity, isEmpty, kebabCase } from 'lodash';
import { DateTime } from 'luxon';
import { createBemBlockBuilder, Loading } from '@ebsco-ui/ebsco-ui';

import {
  AddToProjectModalProvider,
  FeatureFlags,
  PageErrorMessage,
  RecordSummary,
  SearchInstanceDto,
  SectionWithTitle,
  useRecentActivityHistory,
} from '@app/components';
import {
  useAppContext,
  useAvailabilityContext,
  useMappingsContext,
  useSearchContext,
} from '@app/contexts';
import { useFeatureFlag, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { getItemStatusData, getItemWithStatus, makeRequest } from '@app/utils';
import { sharedMessages } from '@app/translations';
import { Feature } from '@app/constants';

import { INSTANCE_DETAILS_TABS, Tabs } from './Tabs';
import { Novelist, NovelistProps } from './Novelist';
import { AvailabilityTable } from './AvailabilityTable';
import { Carousel } from './Carousel';
import { useVirtualShelfFetch } from './useVirtualShelfFetch';
import { PreviousNextInstancePagination } from './PreviousNextInstancePagination';
import { RelatedSubjects } from './RelatedSubjects';
import { useLCLinkResolverQuery } from './useLCLinkResolverQuery';

import './Tabs/RecordMetadata/RecordMetadata.scss';
import './InstanceDetailsPage.scss';

const cnBem = createBemBlockBuilder(['instanceDetailsPage']);
const cnBemRecordMetadata = createBemBlockBuilder(['recordMetadata']);

export const InstanceDetailsPage: FC = () => {
  const intl = useIntl();
  const { $t } = intl;
  const { getAvailability, availability, isAvailabilityLoading } =
    useAvailabilityContext();
  const { locale } = useAppContext();
  const { isActive: isVirtualShelfFlagActive } = useFeatureFlag({
    name: Feature.virtualBookshelf,
  });
  const { isActive: isLCBrowseShelfAndRelatedSubjectsPlacementFlagActive } =
    useFeatureFlag({ name: Feature.LCBrowseShelfAndRelatedSubjectsPlacement });
  const { selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption } =
    useHoldsAndCheckoutsPageConfig();
  const { userItemStatuses } = useMappingsContext();
  const { setIsPageFromBackButton } = useSearchContext();
  const { id = '' } = useParams();
  const { addViewedRecord } = useRecentActivityHistory();
  const [browseShelfOrder, setBrowseShelfOrder] = useState<string>();
  const [currentTab, setCurrentTab] = useState(
    INSTANCE_DETAILS_TABS.fullRecord
  );
  const {
    relatedItems,
    virtualShelfQuery: { isInitialLoading: isVirtualShelfLoading },
  } = useVirtualShelfFetch(
    isVirtualShelfFlagActive ? browseShelfOrder : undefined
  );
  const {
    searchResults: { pages },
  } = useSearchContext();
  const instanceDetailsQuery = useQuery<SearchInstanceDto>(
    ['instanceDetails', { id }],
    () =>
      makeRequest<SearchInstanceDto>({
        url: `/opac-inventory/instances/${id}`,
        params: { withHoldingAndItemData: true },
      })
  );
  const LCLinkResolverQuery = useLCLinkResolverQuery(id);
  // Set `fetchedPages` to equal `pages` on mount, this state is preserved until user navigates away from the page
  // using previous/next pagination new pages are fetched outside of search provider and stored to `fetchedPages`
  const [fetchedPages, setFetchedPages] = useState<typeof pages>(pages);

  const { data: instanceData } = instanceDetailsQuery;
  const isDataFetched = instanceData && !isEmpty(instanceData);

  useEffect(() => {
    if (instanceData && !isEmpty(instanceData)) {
      setBrowseShelfOrder(instanceData.effectiveShelvingOrder);
    }
  }, [instanceData]);

  const handlePopState = useCallback(
    () => setIsPageFromBackButton(true),
    [setIsPageFromBackButton]
  );

  useEventListener('popstate', handlePopState);

  const browseShelfElements = useMemo(
    () => [
      {
        testId: 'browseShelf',
        title: $t({
          id: 'instanceDetails.browseShelf',
          defaultMessage: 'Browse this shelf',
        }),
        shouldRender:
          isVirtualShelfFlagActive &&
          (isVirtualShelfLoading || !isEmpty(relatedItems)),
        getData: () => ({ relatedItems, isVirtualShelfLoading }),
        formatter: data =>
          data.isVirtualShelfLoading ? (
            <Loading
              loadingMessage={$t({
                id: 'shelf.loading',
                defaultMessage: 'Loading shelf',
              })}
            />
          ) : (
            <Carousel items={data.relatedItems} />
          ),
      },
      {
        title: $t({
          id: 'instanceDetails.relatedSubjects',
          defaultMessage: 'Related subjects',
        }),
        getData: instance => instance.subjects,
        formatter: data => <RelatedSubjects subjects={data} />,
      },
    ],
    [$t, isVirtualShelfFlagActive, isVirtualShelfLoading, relatedItems]
  );

  const renderBrowseShelf = () => {
    if (currentTab !== INSTANCE_DETAILS_TABS.fullRecord) {
      return null;
    }

    return (
      <div
        data-auto="browse-shelf"
        className={cn(cnBem('__fullWidthWrapper'), cnBem('__browseShelf'))}
      >
        <div className={cn(cnBem(), 'container')}>
          {browseShelfElements.map(
            ({
              title,
              shouldRender = true,
              testId = kebabCase(title),
              formatter = identity,
              getData,
            }) =>
              shouldRender &&
              !isEmpty(getData(instanceData)) && (
                <SectionWithTitle title={title} key={title} testId={testId}>
                  <div
                    className={cn(
                      cnBemRecordMetadata(),
                      cnBemRecordMetadata('__content'),
                      {
                        [cnBemRecordMetadata('__content--fullWidth')]:
                          testId === 'browseShelf',
                      }
                    )}
                    dir="ltr"
                    data-auto={`record-${testId}-content`}
                  >
                    {formatter(getData(instanceData))}
                  </div>
                </SectionWithTitle>
              )
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!availability[id]) {
      getAvailability([id]);
    }
  }, [id, availability, getAvailability]);

  const itemStatusData = useMemo(() => {
    const itemWithStatus =
      instanceData && getItemWithStatus(instanceData.id, userItemStatuses);

    return (
      itemWithStatus &&
      getItemStatusData(
        itemWithStatus,
        intl,
        locale,
        selectConfiguredHoldsAndCheckoutsOption
      )
    );
  }, [
    instanceData,
    intl,
    locale,
    selectConfiguredHoldsAndCheckoutsOption,
    userItemStatuses,
  ]);

  useEffect(() => {
    addViewedRecord({ instanceId: id, timestamp: `${DateTime.now()}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const renderPageContent = () => {
    const { isError, isLoading, refetch } = instanceDetailsQuery;

    if (isLoading || LCLinkResolverQuery.isInitialLoading) {
      return (
        <main data-auto="spinner">
          <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
        </main>
      );
    }

    if (isError) {
      return (
        <main>
          <PageErrorMessage onClick={refetch} />
        </main>
      );
    }

    const recordAvailability = availability[id];
    const shouldDisplayAvailabilityTable =
      !isEmpty(recordAvailability?.availabilityData) &&
      recordAvailability?.summaryAvailability.isRTACApplicable;
    const availabilityLoadingMessage = $t({
      id: 'availability.loading',
      defaultMessage: 'Checking availability',
    });

    if (isDataFetched) {
      return (
        <>
          <Helmet>
            <title>
              {$t(
                {
                  id: 'instanceDetails.documentTitle',
                  defaultMessage: '{title} - Record details - EBSCO Locate',
                },
                { title: instanceData.title }
              )}
            </title>
          </Helmet>
          <main data-auto="instance-details-container">
            <div
              data-auto="rtac-table"
              className={cn(cnBem('__wrapper'), 'container')}
            >
              <AddToProjectModalProvider>
                <RecordSummary
                  instance={instanceData}
                  itemStatusData={itemStatusData}
                  availability={recordAvailability?.summaryAvailability}
                  isAvailabilityLoading={isAvailabilityLoading}
                  availabilityLoadingMessage={availabilityLoadingMessage}
                  placement="instanceDetails"
                  testId="record-summary"
                  LCLinkResolverLink={LCLinkResolverQuery.data}
                />
              </AddToProjectModalProvider>
              {isAvailabilityLoading ? (
                <div className={cnBem('__availabilityLoading')}>
                  <Loading loadingMessage={availabilityLoadingMessage} />
                </div>
              ) : (
                shouldDisplayAvailabilityTable && (
                  <SectionWithTitle
                    title={$t({
                      id: 'instanceDetails.availabilityLocations',
                      defaultMessage: 'Availability & locations',
                    })}
                  >
                    <AvailabilityTable
                      records={recordAvailability.availabilityData}
                      maxAmountToDisplay={3}
                    />
                  </SectionWithTitle>
                )
              )}
            </div>
            {!isLCBrowseShelfAndRelatedSubjectsPlacementFlagActive &&
              renderBrowseShelf()}
            <div
              data-auto="full-instance-data"
              className={cn(cnBem('__wrapper'), 'container')}
            >
              <Tabs instance={instanceData} onTabChange={setCurrentTab} />
            </div>
            {isLCBrowseShelfAndRelatedSubjectsPlacementFlagActive &&
              renderBrowseShelf()}
            <div
              className={cn(
                cnBem('__fullWidthWrapper'),
                cnBem('__prevNextPaginationWrapper')
              )}
            >
              <div className={cn(cnBem(), 'container')}>
                <PreviousNextInstancePagination
                  instanceId={id}
                  pages={fetchedPages}
                  setPages={setFetchedPages}
                />
              </div>
            </div>
          </main>
          <FeatureFlags
            flagsToMatch={[{ name: Feature.novelist }]}
            renderOn={flags => (
              <Novelist
                isbn={instanceData?.isbns?.[0]}
                config={flags[0].config as unknown as NovelistProps['config']}
              />
            )}
          />
        </>
      );
    }

    return null;
  };

  return (
    <div data-auto="instance-details-page-wrapper">{renderPageContent()}</div>
  );
};
